import { createSignal, Show } from "solid-js";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useI18n } from "@/providers/i18n-provider";
import { api, createApiQuery } from "@/lib/api";
import { useExecuteSignOut } from "@/lib/auth";
import { sharedApi } from "@listto/shared";
import { msg } from "@lingui/core/macro";
import { AlertTriangle, Loader2, Trash2, Undo2 } from "lucide-solid";

/**
 * Zona de peligro del perfil: eliminación de cuenta con periodo de gracia.
 *
 * - Solicitar la eliminación NO borra la cuenta: el server la marca, avisa por
 *   email y cierra todas las sesiones. Tras el periodo de gracia (30 días por
 *   defecto) se purga definitivamente.
 * - Si hay una eliminación pendiente (el usuario volvió a iniciar sesión
 *   dentro del periodo), se muestra la fecha y un botón para cancelarla.
 */
export function DangerZoneSection() {
  const { t } = useI18n();
  const executeSignOut = useExecuteSignOut();
  const [busy, setBusy] = createSignal(false);

  const statusQuery = createApiQuery(
    () => ["account-deletion-status"],
    async () => sharedApi.getDeletionStatus(api),
    () => ({ suspense: false }),
  );

  const purgeAt = () => statusQuery.data?.purgeAt ?? null;
  const pending = () => Boolean(purgeAt());
  const purgeDate = () => {
    const at = purgeAt();
    return at ? new Date(at).toLocaleDateString() : "";
  };

  const requestDeletion = () => {
    void (async () => {
      setBusy(true);
      try {
        await sharedApi.requestAccountDeletion(api);
        // El server ya cerró todas las sesiones: limpieza local + /login.
        executeSignOut();
      } finally {
        setBusy(false);
      }
    })();
  };

  const cancelDeletion = () => {
    void (async () => {
      setBusy(true);
      try {
        await sharedApi.restoreAccount(api);
        await statusQuery.refetch();
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <Card class="border-destructive/30">
      <CardHeader class="pb-2">
        <div class="flex items-center gap-2">
          <AlertTriangle class="h-5 w-5 text-destructive" />
          <CardTitle>{t(msg`Delete account`)}</CardTitle>
        </div>
      </CardHeader>
      <CardContent class="space-y-4">
        <Show
          when={pending()}
          fallback={
            <>
              <p class="text-sm text-muted-foreground">
                {t(
                  msg`Your account and all its data will be permanently deleted after a 30-day grace period. You can recover it by signing in and cancelling the deletion before that date.`,
                )}
              </p>
              <ConfirmDialog
                title={t(msg`Delete account`)}
                description={t(
                  msg`Your account will be scheduled for permanent deletion and you will be signed out everywhere. You can recover it by signing in and cancelling the deletion within the next 30 days. After that, all your data will be permanently deleted.`,
                )}
                confirmLabel={t(msg`Delete my account`)}
                variant="destructive"
                isLoading={busy()}
                onConfirm={requestDeletion}
                renderTrigger={(open) => (
                  <Button
                    variant="destructive"
                    class="w-full rounded-2xl gap-2 h-12"
                    onClick={open}
                    disabled={busy() || statusQuery.isPending}
                    data-testid="delete-account-button"
                  >
                    <Show when={!busy()} fallback={<Loader2 class="h-5 w-5 animate-spin" />}>
                      <Trash2 class="h-5 w-5" />
                    </Show>
                    {t(msg`Delete account`)}
                  </Button>
                )}
              />
            </>
          }
        >
          <div
            class="flex items-start gap-3 rounded-xl border border-destructive/20 bg-destructive/5 p-3"
            data-testid="deletion-pending-banner"
          >
            <AlertTriangle class="mt-0.5 h-4 w-4 text-destructive" />
            <div class="space-y-1">
              <p class="text-sm font-bold text-destructive">{t(msg`Account deletion scheduled`)}</p>
              <p class="text-xs text-muted-foreground">
                {t(msg`Your account will be permanently deleted on {{date}}.`, {
                  date: purgeDate(),
                })}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            class="w-full rounded-2xl gap-2 h-12"
            onClick={cancelDeletion}
            disabled={busy()}
            data-testid="cancel-deletion-button"
          >
            <Show when={!busy()} fallback={<Loader2 class="h-5 w-5 animate-spin" />}>
              <Undo2 class="h-5 w-5" />
            </Show>
            {t(msg`Cancel deletion and keep my account`)}
          </Button>
        </Show>
      </CardContent>
    </Card>
  );
}
